import React, { memo } from "react";
import styled from "styled-components";
import { theme } from "uikit";

import useFormatDate from "../../hooks/useFormatDate";
import { useTypedSelector } from "../../redux/store";
import { Language } from "../../assets/languages/langs";
import { needsExternalCheck } from "../../utils/connectionQuality";

import useConnectionStats from "./useConnectionStats";
import useConnectionText from "./useConnectionText";
import { hasConnectionHint, isNoLink } from "./constants";

const Root = styled.div`
	min-width: 300px;
	max-width: 420px;
	padding: 14px 16px 16px;

	font-family: Lato;
	color: ${theme.colors.primary};
`;

const Title = styled.div`
	font-size: 14px;
	font-weight: 600;
	line-height: 18px;
`;

const Hint = styled.div`
	margin-top: 6px;

	font-size: 12px;
	line-height: 16px;

	color: #5e6b73;
`;

const Facts = styled.dl`
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: 16px;
	row-gap: 6px;

	margin: 12px 0 0;
	padding-top: 12px;

	border-top: 1px solid #e3e6e8;

	font-size: 13px;
	line-height: 17px;

	dt {
		color: #8b969e;
	}

	dd {
		margin: 0;

		text-align: right;
		white-space: nowrap;
		font-variant-numeric: tabular-nums;
	}
`;

const Details = memo(({ lang }: Details.Props) => {
	const t = useConnectionText(lang);
	const formatDate = useFormatDate();
	const stats = useConnectionStats();

	const backendIp = useTypedSelector((state) => state.currentIP.value);
	const { reconnects, lastCheckAt, externalIp, external } = useTypedSelector(
		(state) => state.connectionQuality,
	);

	const { level, lost, total } = stats;
	// Without a link the last answered ping is history, not the current delay.
	const median = isNoLink(level) ? null : stats.median;

	const showInternet =
		!!external && (needsExternalCheck(level) || level === "offline");

	let ip = externalIp ?? "—";
	if (backendIp) ip = `${ip} (${backendIp})`;

	return (
		<Root>
			<Title>
				{t("title")}: {t(`level.${level}`)}
			</Title>
			{hasConnectionHint(level) && <Hint>{t(`hint.${level}`)}</Hint>}
			<Facts>
				<dt>{t("ping")}</dt>
				<dd>
					{median === null
						? "—"
						: t("ms", { value: Math.round(median) })}
				</dd>
				<dt>{t("lost")}</dt>
				<dd>{t("lostValue", { lost, total })}</dd>
				<dt>{t("reconnects")}</dt>
				<dd>{reconnects}</dd>
				<dt>{t("checkedAt")}</dt>
				<dd>
					{lastCheckAt ? formatDate(lastCheckAt, "HH:mm:ss") : "—"}
				</dd>
				{showInternet && external && (
					<>
						<dt>{t("internet")}</dt>
						<dd>{t(`internetVerdict.${external.verdict}`)}</dd>
					</>
				)}
				<dt>{t("ip")}</dt>
				<dd>{ip}</dd>
			</Facts>
		</Root>
	);
});

declare namespace Details {
	interface Props {
		/** Built-in texts in this language (login screen); else the loaded translations. */
		lang?: Language;
	}
}

export default Details;
